import { useEffect, useMemo, useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button, BrandLoading } from '@scripsteam/scrips-signal-ds';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { FieldLabel } from '@scripsteam/scrips-signal-ds';
import {
  useEducationWorkExperience,
  useSaveEducationWorkExperience,
} from '@/settings/services';
import type {
  EducationAchievement,
  EducationDurationYears,
  EducationQualification,
  EducationResidencyOrFellowship,
  EducationWorkExperience,
  EducationWorkExperienceEntry,
} from '@/types/settings-api';
import { QualificationEditDialog } from '../education/qualification-edit-dialog';
import { WorkExperienceEditDialog } from '../education/work-experience-edit-dialog';
import { ResidencyFellowshipEditDialog } from '../education/residency-fellowship-edit-dialog';
import type { ResidencyFellowshipKind } from '../education/residency-fellowship-edit-dialog';
import { SETTINGS_SECTION_TITLE } from '@/settings/components/settings-look';

/**
 * Educational Details tab — PROD-761 commits 4 + 10 + 11 + 12.
 *
 * GET wired: displays all 5 sections from
 * `/api/EducationWorkExperience/{doctorSetupId}`.
 *
 * Edit coverage (complete after commit 12):
 *   - Achievements: full CRUD (commit 4 — bare text, no typeahead)
 *   - Qualifications: full CRUD (commit 10 — typeaheads on Master/MedicalSchools
 *     and Master/DegreeValueSet, year dropdown for graduation year)
 *   - Work Experiences: full CRUD (commit 11 — Organisation + Position free text,
 *     Start Year + End Year dropdowns with End >= Start constraint)
 *   - Residencies + Fellowships: full CRUD (commit 12 — Master/Hospital +
 *     SNOMED speciality typeaheads, shared dialog parameterised on `kind`)
 *
 * Save dispatches POST with the full record — every section's existing
 * entries are preserved verbatim across saves so editing one section
 * never clears another.
 *
 * Wire-shape preserved verbatim (see settings-api.ts):
 *   - Typo'd field names: `orgnizationName` / `orgnizationId`
 *   - Year ranges (integers), not dates
 *   - `id: null` on new rows survives JSON.stringify; backend allocates
 *     the join PK server-side. Existing rows are spliced in-place by
 *     `id`. Removed rows are filtered out of the array (no soft-delete
 *     flag on EducationWorkExperience entries — backend replaces the
 *     full array on save).
 */
export interface PractitionerEducationalTabProps {
  doctorSetupId: string;
  canEdit: boolean;
}

export function PractitionerEducationalTab({
  doctorSetupId,
  canEdit,
}: PractitionerEducationalTabProps) {
  const { data, isLoading } = useEducationWorkExperience(doctorSetupId);
  const saveMutation = useSaveEducationWorkExperience();

  const [source, setSource] = useState<EducationWorkExperience | null>(null);
  const [draft, setDraft] = useState<EducationWorkExperience | null>(null);

  useEffect(() => {
    if (data) {
      setSource(data);
      setDraft(data);
    }
  }, [data]);

  const isDirty = useMemo(() => {
    if (!source || !draft) return false;
    return JSON.stringify(source) !== JSON.stringify(draft);
  }, [source, draft]);

  if (isLoading || !draft) {
    return (
      <BrandLoading size={48} label="Loading educational details…" />
    );
  }

  const setAchievements = (next: EducationAchievement[]) => {
    setDraft({ ...draft, achievements: next });
  };

  const setQualifications = (next: EducationQualification[]) => {
    setDraft({ ...draft, qualifications: next });
  };

  const setWorkExperiences = (next: EducationWorkExperienceEntry[]) => {
    setDraft({ ...draft, workExperiences: next });
  };

  const setResidencies = (next: EducationResidencyOrFellowship[]) => {
    setDraft({ ...draft, residencies: next });
  };

  const setFellowships = (next: EducationResidencyOrFellowship[]) => {
    setDraft({ ...draft, fellowships: next });
  };

  const handleSave = () => {
    saveMutation.mutate(draft, {
      onSuccess: () => toast.success('Educational details saved'),
      onError: (err) =>
        toast.error(err instanceof Error ? err.message : 'Save failed'),
    });
  };

  return (
    <div className="space-y-6">
      <h2 className={SETTINGS_SECTION_TITLE}>
        Qualifications
      </h2>
      <PlainCard>
        <QualificationsEditor
          items={draft.qualifications}
          canEdit={canEdit}
          onChange={setQualifications}
        />
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Work experiences
      </h2>
      <PlainCard>
        <WorkExperiencesEditor
          items={draft.workExperiences}
          canEdit={canEdit}
          onChange={setWorkExperiences}
        />
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Residencies
      </h2>
      <PlainCard>
        <ResidencyFellowshipEditor
          kind="residency"
          items={draft.residencies}
          canEdit={canEdit}
          onChange={setResidencies}
        />
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Fellowships
      </h2>
      <PlainCard>
        <ResidencyFellowshipEditor
          kind="fellowship"
          items={draft.fellowships}
          canEdit={canEdit}
          onChange={setFellowships}
        />
      </PlainCard>

      <h2 className={SETTINGS_SECTION_TITLE}>
        Achievements
      </h2>
      <PlainCard>
        <AchievementsEditor
          items={draft.achievements}
          canEdit={canEdit}
          onChange={setAchievements}
        />
      </PlainCard>

      {canEdit && (
        <div className="flex justify-end">
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isDirty || saveMutation.isPending}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save Educational Details'}
          </Button>
        </div>
      )}
    </div>
  );
}

function PlainCard({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-border-hairline bg-surface-card shadow-1 px-6 py-6 flex flex-col gap-4">
      {children}
    </section>
  );
}


function formatDuration(d: EducationDurationYears): string {
  // Treat 0 as null. Year 0 doesn't exist in the Gregorian calendar
  // (1 BC → 1 AD with no zero) so a backend value of 0 always means
  // "missing/unset", not a real year. Defensive per @samer review on
  // PR #115 — cheap to avoid rendering "2020 – 0" if a 0 ever leaks
  // through from legacy data.
  const start = d.start === 0 ? null : d.start;
  const end = d.end === 0 ? null : d.end;
  if (start == null && end == null) return '—';
  if (start == null) return `until ${end}`;
  if (end == null) return `${start} – present`;
  return `${start} – ${end}`;
}

function QualificationsEditor({
  items,
  canEdit,
  onChange,
}: {
  items: EducationQualification[];
  canEdit: boolean;
  onChange: (next: EducationQualification[]) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (next: EducationQualification) => {
    if (editingIndex === null) {
      onChange([...items, next]);
    } else {
      onChange(items.map((q, i) => (i === editingIndex ? next : q)));
    }
  };

  const handleRemove = () => {
    if (editingIndex === null) return;
    onChange(items.filter((_, i) => i !== editingIndex));
  };

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">No qualifications recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((q, i) => (
            <li
              key={q.id ?? `qual-new-${i}`}
              data-slot="settings-record"
              className="flex items-start justify-between gap-3 rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1"
            >
              <div className="flex-1 text-sm text-text-default">
                <p data-slot="settings-record-title" className="font-semibold">
                  {q.degreeName || q.code.displayName || '—'}
                </p>
                <p className="text-text-muted">
                  {q.issuerName || '—'} · {formatDuration(q.duration)}
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleEdit(i)}
                  aria-label={`Edit ${q.degreeName || 'qualification'}`}
                  className="icon-action"
                >
                  <Pencil className="size-4" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <Button
          type="button"
          variant="tertiary"
          data-slot="settings-add"
          onClick={handleAdd}
          className="text-brand-primary"
        >
          <Plus className="size-4 mr-2" />
          Add Qualification
        </Button>
      )}

      <QualificationEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editingIndex !== null ? items[editingIndex] : null}
        onSave={handleSave}
        onRemove={editingIndex !== null ? handleRemove : undefined}
      />
    </div>
  );
}

function WorkExperiencesEditor({
  items,
  canEdit,
  onChange,
}: {
  items: EducationWorkExperienceEntry[];
  canEdit: boolean;
  onChange: (next: EducationWorkExperienceEntry[]) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (next: EducationWorkExperienceEntry) => {
    if (editingIndex === null) {
      onChange([...items, next]);
    } else {
      onChange(items.map((e, i) => (i === editingIndex ? next : e)));
    }
  };

  const handleRemove = () => {
    if (editingIndex === null) return;
    onChange(items.filter((_, i) => i !== editingIndex));
  };

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">No work experience recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((e, i) => (
            <li
              key={e.id ?? `we-new-${i}`}
              data-slot="settings-record"
              className="flex items-start justify-between gap-3 rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1"
            >
              <div className="flex-1 text-sm text-text-default">
                <p data-slot="settings-record-title" className="font-semibold">{e.position || '—'}</p>
                <p className="text-text-muted">
                  {e.orgnizationName || '—'} · {formatDuration(e.duration)}
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleEdit(i)}
                  aria-label={`Edit ${e.position || 'work experience'}`}
                  className="icon-action"
                >
                  <Pencil className="size-4" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <Button
          type="button"
          variant="tertiary"
          data-slot="settings-add"
          onClick={handleAdd}
          className="text-brand-primary"
        >
          <Plus className="size-4 mr-2" />
          Add Work Experience
        </Button>
      )}

      <WorkExperienceEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editingIndex !== null ? items[editingIndex] : null}
        onSave={handleSave}
        onRemove={editingIndex !== null ? handleRemove : undefined}
      />
    </div>
  );
}

function ResidencyFellowshipEditor({
  kind,
  items,
  canEdit,
  onChange,
}: {
  kind: ResidencyFellowshipKind;
  items: EducationResidencyOrFellowship[];
  canEdit: boolean;
  onChange: (next: EducationResidencyOrFellowship[]) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleSave = (next: EducationResidencyOrFellowship) => {
    if (editingIndex === null) {
      onChange([...items, next]);
    } else {
      onChange(items.map((r, i) => (i === editingIndex ? next : r)));
    }
  };

  const handleRemove = () => {
    if (editingIndex === null) return;
    onChange(items.filter((_, i) => i !== editingIndex));
  };

  const label = kind === 'residency' ? 'Residency' : 'Fellowship';

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">
          No {kind} recorded.
        </p>
      ) : (
        <ul className="space-y-2">
          {items.map((r, i) => (
            <li
              key={r.id ?? `${kind}-new-${i}`}
              data-slot="settings-record"
              className="flex items-start justify-between gap-3 rounded-md border border-border-card bg-surface-card px-3 py-2.5 shadow-1"
            >
              <div className="flex-1 text-sm text-text-default">
                <p data-slot="settings-record-title" className="font-semibold">
                  {r.specialityName || r.code.displayName || '—'}
                </p>
                <p className="text-text-muted">
                  {r.orgnizationName || '—'} · {formatDuration(r.duration)}
                </p>
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => handleEdit(i)}
                  aria-label={`Edit ${r.specialityName || label.toLowerCase()}`}
                  className="icon-action"
                >
                  <Pencil className="size-4" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <Button
          type="button"
          variant="tertiary"
          data-slot="settings-add"
          onClick={handleAdd}
          className="text-brand-primary"
        >
          <Plus className="size-4 mr-2" />
          Add {label}
        </Button>
      )}

      <ResidencyFellowshipEditDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        kind={kind}
        initial={editingIndex !== null ? items[editingIndex] : null}
        onSave={handleSave}
        onRemove={editingIndex !== null ? handleRemove : undefined}
      />
    </div>
  );
}

function AchievementsEditor({
  items,
  canEdit,
  onChange,
}: {
  items: EducationAchievement[];
  canEdit: boolean;
  onChange: (next: EducationAchievement[]) => void;
}) {
  const setItem = (index: number, text: string) => {
    onChange(
      items.map((it, i) =>
        i === index ? { ...it, achievementText: text } : it,
      ),
    );
  };
  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };
  const addItem = () => {
    onChange([...items, { id: null, achievementText: '' }]);
  };

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-sm text-text-muted">No achievements recorded.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((a, i) => (
            <li
              key={a.id ?? `achievement-${i}`}
              className="flex items-start gap-2"
            >
              <div className="flex-1">
                <FieldLabel stacked>Achievement #{i + 1}</FieldLabel>
                <TextField
                  value={a.achievementText}
                  disabled={!canEdit}
                  onChange={(e) => setItem(i, e.target.value)}
                  placeholder="e.g. Best Practitioner Award 2026"
                />
              </div>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => removeItem(i)}
                  aria-label={`Remove achievement ${i + 1}`}
                  className="icon-action mt-6"
                >
                  <Trash2 className="size-4" aria-hidden />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && (
        <Button
          type="button"
          variant="tertiary"
          data-slot="settings-add"
          onClick={addItem}
          className="text-brand-primary"
        >
          <Plus className="size-4 mr-2" />
          Add Achievement
        </Button>
      )}
    </div>
  );
}
